import { Component, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ThemeService } from '../../service/theme.service';
import { Subscription } from 'rxjs';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import type * as monaco from 'monaco-editor';

@Component({
	selector: 'app-email-body-editor',
	templateUrl: `./email-body-editor.component.html`,
	styleUrls: ['./email-body-editor.component.scss'],
	standalone: true,
	imports: [CommonModule, MonacoEditorModule, FormsModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => EmailBodyEditorComponent),
			multi: true
		}
	]
})
export class EmailBodyEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
	@Input() placeholders: { [key: string]: string } = {};
	valueInternal = '';
	subscription = new Subscription();
	editorInstance!: monaco.editor.IStandaloneCodeEditor;
	editorOptions!: monaco.editor.IStandaloneEditorConstructionOptions

	// private static modelPlaceholders = new Map<string, { [key: string]: string }>();
	// private static registered = false;
	private onChange: (value: string) => void = () => { };
	private onTouched: () => void = () => { };

	constructor(private themeService: ThemeService) {
		this.editorOptions = {
			language: 'emailLang',
			theme: this.themeService.getTheme() === 'dark' ? 'emailThemeDark' : 'emailThemeLight',
			automaticLayout: true,
			minimap: { enabled: false },
			stickyScroll: { enabled: false },
			folding: false,
		};
	}
	ngOnInit(): void {
		this.subscription.add(
			this.themeService.theme$.subscribe(theme => {
				const mon = (window as any).monaco;
				if (mon && mon.editor && mon.editor.setTheme) {
					console.log('Set theme on monaco', theme);
					mon.editor.setTheme(theme === 'dark' ? 'emailThemeDark' : 'emailThemeLight');
				} else {
					console.log('Set theme on editorOptions', theme);
					this.editorOptions = { ...this.editorOptions, theme: theme === 'dark' ? 'emailThemeDark' : 'emailThemeLight' };
				}
			})
		);
	}
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	onEditorInit(editorInstance: monaco.editor.IStandaloneCodeEditor) {
		const monaco = (window as any).monaco;
		if (!monaco) {
			console.error('Monaco not available on window.monaco — ensure MonacoEditorModule.forRoot() has been configured and assets are present.');
			return;
		}
		this.editorInstance = editorInstance;

		if ((window as any).__emailLangRegistered) {
			console.log('Alteady Registered emailLang');
		}else{
			(window as any).__emailLangRegistered = true;
			console.log('Registering emailLang');

			const placeholders = Object.entries(this.placeholders).reduce((acc, [key, value]) => { acc[`{{${key}}}`] = value; return acc; }, {} as { [key: string]: string });
			const languageId = 'emailLang';
			monaco.languages.register({ id: languageId });

			monaco.languages.setMonarchTokensProvider(languageId, {
				tokenizer: {
					root: [
						[/\{\{/, 'placeholder.brace', '@placeholderInside'],
						[/[a-zA-Z_]\w*/, 'identifier'],
						[/[{}()\[\]]/, '@brackets'],
						[/\s+/, 'white']
					],

					placeholderInside: [
						[/\}\}/, 'placeholder.brace', '@pop'],

						[/[^}]+/, {
							cases: {
								'@validPlaceholders': 'placeholder.content',
								'@default': 'invalidPlaceholder'
							}
						}],

						[/[^}]+/, 'invalidPlaceholder']
					]
				},

				validPlaceholders: Object.keys(this.placeholders).flatMap(
					k => [k.toUpperCase(), k.toLowerCase()]
				)
			});

			monaco.editor.defineTheme('emailThemeLight', {
				base: 'vs',
				inherit: true,
				rules: [
					{ token: 'placeholder.brace', foreground: '096dd9' },
					{ token: 'placeholder.content', foreground: '389e0d' },
					{ token: 'invalidPlaceholder', foreground: 'ff4d4f' },
					{ token: 'identifier', foreground: '000000' }
				],
				colors: {}
			});

			monaco.editor.defineTheme('emailThemeDark', {
				base: 'vs-dark',
				inherit: true,
				rules: [
					{ token: 'placeholder.brace', foreground: '3c9ae8' },
					{ token: 'placeholder.content', foreground: '389e0d' },
					{ token: 'invalidPlaceholder', foreground: 'ff7875' },
					{ token: 'identifier', foreground: 'ffffff' }
				],
				colors: {}
			});

			monaco.languages.registerCompletionItemProvider(languageId, {
				triggerCharacters: ['{'],
				provideCompletionItems: (model: monaco.editor.ITextModel, position: monaco.Position) => {
					const startCol = Math.max(1, position.column - 2);
					const textBefore = model.getValueInRange({
						startLineNumber: position.lineNumber,
						startColumn: startCol,
						endLineNumber: position.lineNumber,
						endColumn: position.column
					});

					if (textBefore !== '{{') return { suggestions: [] };

					return {
						suggestions: Object.keys(placeholders).map((key) => ({
							label: key,
							kind: monaco.languages.CompletionItemKind.Variable,
							insertText: key,
							range: new monaco.Range(position.lineNumber, position.column - 2, position.lineNumber, position.column),
							detail: placeholders[key],
							documentation: placeholders[key]
						}))
					};
				}
			});

			monaco.languages.registerHoverProvider(languageId, {
				provideHover: (model: monaco.editor.ITextModel, position: monaco.Position) => {
					const line = model.getLineContent(position.lineNumber);
					const matches = line.match(/\{\{[^}]*\}\}/g);
					if (!matches) return { contents: [] };

					for (const token of matches) {
						const startCol = line.indexOf(token) + 1;
						const endCol = startCol + token.length;
						if (position.column >= startCol && position.column <= endCol) {
							const desc = placeholders[token];
							if (!desc) return { contents: [] };
							return {
								range: new monaco.Range(position.lineNumber, startCol, position.lineNumber, endCol),
								contents: [{ value: `**${token}**: ${desc}` }]
							};
						}
					}
					return { contents: [] };
				}
			});
		}
		this.subscription.add(
			this.themeService.theme$.subscribe(theme => {
				monaco.editor.setTheme(theme === 'dark' ? 'emailThemeDark' : 'emailThemeLight');
			})
		);

		// Update form value on content change
		editorInstance.onDidChangeModelContent(() => {
			const val = editorInstance.getValue();
			this.valueInternal = val;
			queueMicrotask(() => this.onChange?.(val));
		});

		// Mark touched
		editorInstance.onDidBlurEditorWidget(() => this.onTouched());
		this.editorInstance.updateOptions({ stickyScroll: { enabled: false }, folding: false });

	}




	writeValue(value: string): void {
		this.valueInternal = value || '';
		if (this.editorInstance) {
			this.editorInstance.setValue(this.valueInternal);
		}
	}
	// onNgModelChange(v: string) {
	// 	this.valueInternal = v;
	// 	this._onChange(v);
	// }
	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		if (this.editorInstance) {
			this.editorInstance.updateOptions({ readOnly: isDisabled });
		}
	}
}
